import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { WithStyles, Theme, createStyles, withStyles } from '@material-ui/core';
import { StoreState } from '../../reducers/root-reducer';
import { Dispatch } from 'redux';
import { closeEditInquiryDialog } from '../../actions/store/app-actions';
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import TimePicker from "material-ui-pickers/TimePicker";
import { formatDate } from "../../util/date-utility";
import { ConnectedDatePicker } from "../datepicker/index";
import { updateInquiry } from '../../actions/inquiry-actions';
import { isIPhoneX } from '../../util/device-util';

interface State {
	date: Date;
	time: Date;
	peopleCount: number;
	area: string;
	primaryName: string;
	email: string;
	phoneNumber: string;
	notes: string;
}

type Props = WithStyles<typeof styles> &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps>;

export class EditInquiryDialog extends React.Component<Props, State> {
	public readonly state: State = {
		date: null,
		time: null,
		peopleCount: 0,
		area: "",
		primaryName: "",
		email: "",
		phoneNumber: "",
		notes: ""
	};

	private form: HTMLFormElement = null;

	public componentDidMount() {
		this.setStateFromProps(this.props);
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (this.props.open === false && nextProps.open === true) {
			this.setStateFromProps(nextProps);
		}
	}

	private setStateFromProps(props: Props) {
		const { inquiry } = props;

		if (!inquiry) return;

		this.setState({
			date: inquiry.date,
			time: inquiry.time,
			peopleCount: inquiry.peopleCount,
			area: inquiry.area,
			primaryName: inquiry.primaryName,
			email: inquiry.email,
			phoneNumber: inquiry.phoneNumber,
			notes: inquiry.notes
		});
	}

	private handleFormRef = (form: HTMLFormElement) => {
		this.form = form;
	};

	private handleDateChange = (date: Date) => {
		this.setState({ date });
	};

	private handleTimeChange = (time: Date) => {
		this.setState({ time });
	};

	private handlePeopleCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const val = Number(event.target.value);

		this.setState({ peopleCount: val > 0 ? val : 0 });
	};

	private handleAreaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		this.setState({ area: event.target.value });
	};

	private handlePrimaryNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ primaryName: event.target.value });
	};

	private handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ email: event.target.value });
	};

	private handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ phoneNumber: event.target.value });
	};

	private handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ notes: event.target.value });
	};

	private handleSubmit = () => {
		if (!this.form.reportValidity()) return;

		updateInquiry(this.props.inquiry.id, this.state);

		this.handleClose();
	};

	private handleClose = () => {
		this.props.close();
	};

	public render() {
		const {
			date,
			time,
			peopleCount,
			area,
			primaryName,
			email,
			phoneNumber,
			notes
		} = this.state;
		const { classes, open, areas, layout, capabilities } = this.props;

		return (
			<Dialog
				className={classes.dialog}
				open={open && date !== null}
				fullScreen={layout === "phone"}
				onClose={this.handleClose}
			>
				<form ref={this.handleFormRef} className={classes.form}>
					<DialogTitle>Anfrage bearbeiten</DialogTitle>
					<DialogContent>
						<div className={classNames(classes.formRow)}>
							<TextField
								type="number"
								className={classNames(
									classes.textField,
									classes.peopleCountInput
								)}
								label="Personenanzahl"
								value={peopleCount}
								onChange={this.handlePeopleCountChange}
								inputProps={{
									min: 1
								}}
								required
							/>
							<FormControl
								className={classNames(
									classes.textField,
									classes.select
								)}
							>
								<InputLabel>Bereich</InputLabel>
								<Select
									value={area}
									onChange={this.handleAreaChange}
									className={classes.areaSelect}
								>
									{areas.map(area => (
										<MenuItem key={area.id} value={area.id}>{area.name}</MenuItem>
									))}
								</Select>
						  </FormControl>
						</div>
						<div className={classes.formRow}>
							<ConnectedDatePicker
								onChange={this.handleDateChange}
								value={date}
								datepickerProps={{
									label: "Datum",
									required: true,
									className: classNames(
										classes.textField,
										classes.datepicker
									),
									InputProps: {
										value: formatDate(date)
									}
								}}
							/>
							<TimePicker
								className={classNames(
									classes.textField,
									classes.timepicker
								)}
								label="Uhrzeit"
								cancelLabel="Abbrechen"
								okLabel="OK"
								value={time}
								onChange={this.handleTimeChange}
								ampm={false}
							/>
						</div>
						<div className={classes.formRow}>
							<TextField
								className={classNames(
									classes.textField,
									classes.primaryNameInput
								)}
								label="Name"
								value={primaryName}
								onChange={this.handlePrimaryNameChange}
								required
							/>
							<TextField
								className={classNames(
									classes.textField,
									classes.phoneNumberInput
								)}
								id="reservationPhoneNumber"
								label="Telefonnummer"
								value={phoneNumber}
								onChange={this.handlePhoneNumberChange}
							/>
						</div>
						<div className={classes.formRow}>
							<TextField
								className={classNames(
									classes.textField,
									classes.emailInput
								)}
								id="reservationEmail"
								label="E-Mail Adresse"
								value={email}
								type="email"
								fullWidth
								required
								onChange={this.handleEmailChange}
							/>
						</div>
						<div className={classes.formRow}>
							<TextField
								className={classNames(
									classes.textField,
									classes.notesInput
								)}
								id="reservationNotes"
								label="Notizen &amp; Anmerkungen"
								fullWidth
								multiline
								rowsMax={4}
								value={notes}
								onChange={this.handleNotesChange}
							/>
						</div>
					</DialogContent>
					<DialogActions>
						<Button
							className={classes.cancelButton}
							onClick={this.handleClose}
						>
							Abbrechen
						</Button>
						<Button
							className={classes.submitButton}
							color="primary"
							onClick={this.handleSubmit}
						>
							Speichern
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	open: state.editInquiryDialogOpen,
	inquiry: state.editInquiryDialogData,
	areas: state.areas,
	capabilities: state.capabilities,
	layout: state.layout,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeEditInquiryDialog())
});

const styles = (theme: Theme) =>
	createStyles({
		dialog: {
			"-webkit-user-select": "none",
			userSelect: "none",
			// pointerEvents: "none"
		},
		dialogClickable: {
			pointerEvents: "all"
		},
		select: {
			width: "calc(50% - 20px)"
		},
		textField: {
			margin: 10,

			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
		form: {
			height: "100%",
			display: "flex",
			flexDirection: "column"
		},
		formRow: {
			display: "flex",
			margin: -10,

			"@media (max-width: 600px)": {
				display: "block"
			}
		},
		peopleCountInput: {},
		areaSelect: {},
		datepicker: {},
		primaryNameInput: {},
		timepicker: {},
		phoneNumberInput: {},
		emailInput: {},
		notesInput: {},
		cancelButton: {},
		submitButton: {}
	});

export const ConnectedEditInquiryDialog = withStyles(styles)(
	connect(mapStateToProps, mapDispatchToProps)(EditInquiryDialog)
);
